import { Keypair, Transaction } from '@solana/web3.js';
import type { SignedBody, UnsignedView } from '@xchange/shared';
import { deriveKey, sign, wipe } from '@xchange/wallet';
import type { Platform } from './platform/index.js';
import { withWallet } from './wallet.js';

/**
 * Signing on the phone. The seed is opened for one signature and wiped.
 *
 * Gajumaru: the server built the raw transaction; the wallet signs the
 * network id followed by those bytes, which is what Groot's node checks.
 * Solana: the server built and fee-signed a transaction; the wallet adds the
 * user's signature and the whole thing goes back.
 */
export type Signed = SignedBody;

export async function signUnsigned(
  platform: Platform,
  pin: string,
  u: UnsignedView,
): Promise<SignedBody> {
  return withWallet(platform, pin, ({ seed }) => {
    if (u.chain === 'gajumaru') {
      const key = deriveKey(seed, 'gajumaru');
      const tx = Uint8Array.from(atob(u.tx), (c) => c.charCodeAt(0));
      const network = new TextEncoder().encode(u.networkId ?? '');
      const message = new Uint8Array(network.length + tx.length);
      message.set(network);
      message.set(tx, network.length);
      const signature = sign(key, message);
      wipe(key.secret);
      return { tx: u.tx, signature: btoa(String.fromCharCode(...signature)) };
    }
    const key = deriveKey(seed, 'solana');
    const keypair = Keypair.fromSeed(key.secret);
    const tx = Transaction.from(Uint8Array.from(atob(u.tx), (c) => c.charCodeAt(0)));
    tx.partialSign(keypair);
    wipe(key.secret, keypair.secretKey);
    return { tx: btoa(String.fromCharCode(...tx.serialize({ requireAllSignatures: true }))) };
  });
}
